import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useConnect, useConfig, useDisconnect } from 'wagmi'
import { disconnect } from '@wagmi/core'
import type { Connector } from 'wagmi'
import { isDesktop } from 'react-device-detect'
import { clearUserData } from 'state/user/actions'
import { clearUserStates } from 'utils/clearUserStates'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useModal } from '@mprolab/uikit'
import { ConnectorName, ConnectorNameEnum, ETH_CHAIN_ID_NUMBER } from '@mprolab/sdk'
import WalletConnectSessionModal from 'components/widgets/WalletConnectSession/WalletConnectSessionModal'
import { walletConnectionStore as walletStore } from 'mobxStores/WalletConnectionStore'
import { walletConnectConfig, saveConnectorToLocalStorage } from 'utils/saveConnector'
import { NETWORK_CONFIG } from 'utils/wallet'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { isSupportedChainId } from 'connectors/utils'
import useToast from './useToast'
import { MPROLAB_HOME_URL } from 'constants/site'
import { isLikelyMissingInjectedProviderError, waitForInjectedProvider } from 'utils/waitForInjectedProvider'
import {
  isConnectionAlreadyPending,
  isConnectionProposalExpired,
  isConnectionRejected,
  isExtensionContextInvalidated
} from 'utils/walletConnectionErrors'
import { reportClientError } from 'utils/reportClientError'
import { wagmiConfig } from 'lib/wagmiConfig'
import { isWalletConnectLike } from 'utils/walletConnectorKind'
import { disconnectWallet } from 'utils/disconnectWallet'

const useAuth = (setWeb3loginInProgress?: (v: boolean) => void) => {
  const { toastSuccess, toastError, toastInfo } = useToast()

  /**
   * Every way this flow can fail leaves a record.
   *
   * Until now a person who could not connect saw a toast and we saw nothing —
   * "I cannot get in" was unanswerable, and a provider that was merely slow to
   * inject looked exactly like one that was absent. Deliberate outcomes
   * (rejection, a request already waiting) are NOT reported: they are not
   * faults, and alerting on them would bury the ones that are.
   */
  const reportConnectFailure = useCallback((reason: string, error?: unknown) => {
    reportClientError({
      kind: 'wallet',
      handled: false,
      route: '/connect',
      message: `wallet connect failed — ${reason}${
        error ? `: ${(error as { message?: string })?.message ?? String(error)}` : ''
      }`
    })
  }, [])
  const { t } = useTranslation()
  const web3 = useWeb3React()
  const dispatch = useDispatch()
  const { setConnectPanelOpen } = useConnectPanel()
  const config = useConfig()
  const { connectAsync } = useConnect()
  const { disconnectAsync } = useDisconnect()

  const [openWcModal, closeWcModal] = useModal(
    <WalletConnectSessionModal
      onModalClose={async () => {
        await disconnectWallet()
        walletStore.setWcPairingUri(undefined)
        if (setWeb3loginInProgress) {
          setWeb3loginInProgress(false)
        }
        walletStore.setModalOpen(false)
        walletStore.setConnectionRejected(true)
        walletStore.handleConnectionRejected()
      }}
    />,
    false
  )

  const login = useCallback(
    async ({
      connectorId,
      title,
      onSuccessCallback,
      onErrorCallback,
      customChainId
    }: {
      connectorId: ConnectorName
      title: string
      onSuccessCallback?: (account?: string, connector?: Connector) => void
      onErrorCallback?: (error: unknown) => void
      customChainId?: number
    }): Promise<Connector | undefined> => {
      await disconnect(wagmiConfig).catch(() => {})
      walletStore.setWcPairingUri(undefined)

      let connId = connectorId
      if (title === ConnectorNameEnum.TRUSTWALLET && walletConnectConfig && isDesktop) {
        connId = walletConnectConfig.connectorId
      }

      saveConnectorToLocalStorage(connId, title)

      const injectedConnector = config.connectors.find((c) => c.id === 'injected')
      const wcConnector = config.connectors.find((c) => c.id === 'walletConnect')

      if (!injectedConnector && connId === ConnectorName.Injected) {
        toastError(t('walletConnect.connectError'))
        reportConnectFailure('no injected connector configured')
        onErrorCallback?.(new Error('No injected connector'))
        return undefined
      }

      if (connId === ConnectorName.Injected && isDesktop) {
        if (typeof window !== 'undefined' && !window.ethereum?.isMetaMask) {
          toastError(t('walletConnect.metamaskNotInstalled'))
          // Worth recording even though it reads as the person's problem: an
          // extension that is merely slow to inject looks identical here, and
          // only the volume of these tells the two apart.
          reportConnectFailure('injected provider not visible at click time')
          onErrorCallback?.(new Error('MetaMask not installed'))
          return undefined
        }
      }

      const currChainId =
        customChainId ||
        web3.provider?._network?.chainId ||
        web3.chainId ||
        parseInt(window?.ethereum?.chainId ?? '0x1', 16)
      const supportedChainId = isSupportedChainId(currChainId) ? currChainId : ETH_CHAIN_ID_NUMBER

      const isWc = connId === ConnectorName.WalletConnect

      try {
        if (isWc) {
          setConnectPanelOpen(false)
          openWcModal()
        }

        let usedConnector: Connector

        if (connId === ConnectorName.Injected) {
          const runInjected = async () => {
            try {
              await connectAsync({
                connector: injectedConnector!,
                chainId: supportedChainId
              })
            } catch (firstErr: unknown) {
              if (isLikelyMissingInjectedProviderError(firstErr)) {
                await waitForInjectedProvider(1500)
                await connectAsync({
                  connector: injectedConnector!,
                  chainId: supportedChainId
                })
                return
              }
              throw firstErr
            }
          }
          await runInjected()
          usedConnector = injectedConnector!
        } else if (isWc) {
          if (!wcConnector) {
            toastError(t('walletConnect.connectError'))
            reportConnectFailure('WalletConnect project id missing')
            onErrorCallback?.(new Error('WalletConnect project id missing'))
            closeWcModal()
            return undefined
          }
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[mprolab/web3] login: WalletConnect (wagmi)', { supportedChainId })
          }
          await connectAsync({ connector: wcConnector, chainId: supportedChainId })
          usedConnector = wcConnector
        } else {
          toastError(t('walletConnect.connectError'))
          reportConnectFailure(`unsupported connector (${title})`)
          return undefined
        }

        if (isWalletConnectLike(usedConnector)) {
          setConnectPanelOpen(true)
        }
        setConnectPanelOpen(false)

        if (isWc) {
          if (title === ConnectorNameEnum.WALLETCONNECT) {
            closeWcModal()
          }
        }

        const acct =
          connId === ConnectorName.Injected
            ? (await injectedConnector!.getAccounts())[0]
            : (await usedConnector.getAccounts())[0]
        onSuccessCallback?.(acct, usedConnector)
        return usedConnector
      } catch (activateError: unknown) {
        if (import.meta.env.DEV && isWc) {
          // eslint-disable-next-line no-console
          console.warn('[mprolab/web3] login: connect failed', activateError)
        }
        console.error(activateError)
        onErrorCallback?.(activateError)

        const code =
          activateError && typeof activateError === 'object' && 'code' in activateError
            ? (activateError as { code?: number }).code
            : undefined

        if (code === 4902) {
          try {
            await window.ethereum?.request({
              method: 'wallet_addEthereumChain',
              params: [NETWORK_CONFIG[ETH_CHAIN_ID_NUMBER]]
            })
            await login({ connectorId: connId, title, onSuccessCallback, onErrorCallback, customChainId })
          } catch (error) {
            console.error(error)
            toastError(t('walletConnect.addNewNetworkRejected'))
          }
        } else if (isConnectionAlreadyPending(activateError)) {
          /**
           * The wallet is already asking. Do NOT disconnect and do NOT call
           * this an error: the request from the first click is sitting in the
           * wallet waiting to be approved, and the only useful thing to say is
           * where it is. Disconnecting here would also throw away the very
           * request the person is about to approve.
           */
          toastInfo(t('walletConnect.approveInWallet'))
        } else if (isConnectionRejected(activateError)) {
          // They closed the popup. That is an answer, not a fault.
          await disconnectWallet()
          closeWcModal()
          toastInfo(t('walletConnect.connectCancelled'))
        } else {
          await disconnectWallet()
          closeWcModal()
          const noMetaMaskInjected = typeof window !== 'undefined' && !window.ethereum?.isMetaMask
          if (
            connId === ConnectorName.Injected &&
            (noMetaMaskInjected || isLikelyMissingInjectedProviderError(activateError))
          ) {
            toastError(t('walletConnect.metamaskNotInstalled'))
            reportConnectFailure(`provider not found (${title})`, activateError)
          } else if (isConnectionProposalExpired(activateError)) {
            // Not a fault: the code on screen timed out. Say that, and let
            // them ask for a new one instead of reading "some errors occurred".
            toastError(t('walletConnect.proposalExpired'))
          } else if (isExtensionContextInvalidated(activateError)) {
            toastError(t('walletConnect.extensionReloaded'))
          } else {
            toastError(t('walletConnect.connectError'))
            reportConnectFailure(`activate failed (${title})`, activateError)
          }
        }
      }
      return undefined
    },
    [
      toastError,
      toastInfo,
      reportConnectFailure,
      t,
      web3.provider?._network?.chainId,
      web3.chainId,
      setConnectPanelOpen,
      openWcModal,
      closeWcModal,
      config.connectors,
      connectAsync
    ]
  )

  const logout = useCallback(
    async ({
      connector,
      networkChanged,
      autoDisconnect
    }: {
      connector: Connector
      networkChanged?: boolean
      autoDisconnect?: boolean
    }) => {
      if (connector) {
        try {
          await disconnectAsync({ connector })

          if (!networkChanged) {
            clearUserStates(true)
            dispatch(clearUserData())
            if (!autoDisconnect) {
              toastSuccess(t('walletConnect.disconnectSuccess'))
              window.setTimeout(() => {
                window.location.replace(`${MPROLAB_HOME_URL}/`)
              }, 200)
            }
          }
        } catch {
          toastError(t('walletConnect.connectError'))
        }
      }
    },
    [toastSuccess, toastError, t, disconnectAsync, dispatch]
  )

  return { login, logout }
}

export default useAuth
